<template>
  <div class="wf-detail" v-loading="loading">
    <template v-if="detail.instance">
      <!-- 标题与状态 -->
      <div class="wf-detail-head">
        <span class="wf-detail-title">{{ detail.instance.title }}</span>
        <el-tag size="small" :type="statusType(detail.instance.status)">{{ statusText(detail.instance.status) }}</el-tag>
      </div>
      <div class="wf-detail-meta">
        发起人：{{ detail.instance.initiatorName || '—' }}
        <span class="wf-sep">|</span> 发起时间：{{ detail.instance.createTime || '—' }}
        <template v-if="detail.instance.endTime"><span class="wf-sep">|</span> 结束时间：{{ detail.instance.endTime }}</template>
      </div>

      <el-tabs v-model="tab" type="border-card" class="wf-detail-tabs">
        <!-- 流程图 -->
        <el-tab-pane label="流程图" name="flow">
          <flow-viewer
            :flow-json="detail.flowSnapshot"
            :instance="detail.instance"
            :tasks="detail.tasks || []"
          />
        </el-tab-pane>

        <!-- 表单内容 -->
        <el-tab-pane label="申请内容" name="form">
          <form-renderer
            :schema="detail.formJson || '{}'"
            :value="detail.formData || {}"
            :readonly="true"
            :user-options="userOptions"
            :dept-options="deptOptions"
            :chance-options="chanceOptions"
            :my-chance-options="myChanceOptions"
            :project-options="projectOptions"
            :my-project-options="myProjectOptions"
            :role-options="roleOptions"
          />
        </el-tab-pane>

        <!-- 审批记录 -->
        <el-tab-pane label="审批记录" name="log">
          <el-timeline>
            <el-timeline-item
              v-for="(l, i) in detail.logs || []"
              :key="i"
              :timestamp="l.createTime"
              :type="logType(l.action)"
              placement="top"
            >
              <div class="wf-log-node">{{ l.nodeName || actionText(l.action) }}</div>
              <div class="wf-log-body">
                <b>{{ l.operatorName || '系统' }}</b>
                <span class="wf-log-action">{{ actionText(l.action) }}</span>
                <span v-if="l.opinion" class="wf-log-opinion">意见：{{ l.opinion }}</span>
              </div>
            </el-timeline-item>
          </el-timeline>
          <div v-if="!(detail.logs && detail.logs.length)" class="wf-empty">暂无记录</div>
        </el-tab-pane>
      </el-tabs>

      <!-- 操作栏 -->
      <div class="wf-detail-actions" v-if="detail.currentTaskId || canRevoke">
        <el-input
          v-if="detail.currentTaskId"
          v-model="opinion"
          type="textarea"
          :rows="2"
          placeholder="审批意见（拒绝时必填）"
          maxlength="500"
          show-word-limit
          style="margin-bottom: 10px"
        />
        <div class="wf-action-btns">
          <el-button v-if="detail.currentTaskId" type="success" icon="el-icon-check" :loading="submitting" @click="doAgree">同意</el-button>
          <el-button v-if="detail.currentTaskId" type="danger" icon="el-icon-close" :loading="submitting" @click="doReject">拒绝</el-button>
          <el-button v-if="canRevoke" type="warning" plain icon="el-icon-refresh-left" :loading="submitting" @click="doRevoke">撤销</el-button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import FlowViewer from './components/FlowViewer.vue'
import FormRenderer from './components/FormRenderer.vue'
import { getDetail, agree, reject, revoke } from '@/api/wflow/process'
import { getUserList } from '@/api/plat/PlatStaff'
import { getDepartmentList } from '@/api/plat/DepMana'
import { getTRoleSelectByPage } from '@/api/plat/PosMana'
import { getSaleChanceOptions, getSaleProjectOptions } from '@/api/sale'

export default {
  name: 'WflowDetail',
  components: { FlowViewer, FormRenderer },
  props: {
    instanceId: { type: [Number, String], default: null }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      tab: 'flow',
      opinion: '',
      detail: {},
      userOptions: [],
      deptOptions: [],
      chanceOptions: [],
      myChanceOptions: [],
      projectOptions: [],
      myProjectOptions: [],
      roleOptions: []
    }
  },
  computed: {
    canRevoke() {
      const inst = this.detail.instance
      return !!(inst && this.detail.initiator && inst.status === 0)
    }
  },
  created() {
    this.loadOrg()
    if (this.instanceId) this.load(this.instanceId)
  },
  methods: {
    load(id) {
      this.loading = true
      getDetail(id).then(res => {
        this.loading = false
        if (res && res.code === 200) this.detail = res.data || {}
      }).catch(() => { this.loading = false })
    },
    loadOrg() {
      getUserList({ page: 1, limit: 2000, condition: {} }).then(res => {
        if (res && res.code === 200 && res.data) this.userOptions = res.data.records || res.data || []
      })
      getDepartmentList().then(res => {
        if (res && res.code === 200) this.deptOptions = this.flattenDept(res.data || [])
      })
      getSaleChanceOptions().then(res => {
        if (res && res.code === 200) this.chanceOptions = res.data || []
      })
      getSaleChanceOptions({ scope: 'mine' }).then(res => {
        if (res && res.code === 200) this.myChanceOptions = res.data || []
      })
      getSaleProjectOptions().then(res => {
        if (res && res.code === 200) this.projectOptions = res.data || []
      })
      getSaleProjectOptions({ scope: 'mine' }).then(res => {
        if (res && res.code === 200) this.myProjectOptions = res.data || []
      })
      getTRoleSelectByPage({ page: 1, limit: 999, roleName: '' }).then(res => {
        if (res && res.code === 200) this.roleOptions = res.data || []
      }).catch(() => {})
    },
    // 扁平化部门树：保留 parentId（层级选择）与 isValid（停用过滤）；
    // 说明：仍保留停用节点，仅用于历史单据的部门名称回显，选择与成员过滤时排除
    flattenDept(tree) {
      const out = []
      const walk = (list, pid) => (list || []).forEach(n => {
        if (!n) return
        // 后端 DepartmentVO 已返回 parentId；缺失时回退用遍历时的父节点 id
        const parentId = n.parentId != null ? n.parentId : (pid != null ? pid : null)
        out.push({ id: n.id, name: n.name, parentId, isValid: n.isValid })
        walk(n.children, n.id)
      })
      walk(tree, null)
      return out
    },
    reload() {
      this.opinion = ''
      this.load(this.instanceId)
      this.$emit('refresh')
    },
    doAgree() {
      this.submitting = true
      agree({ taskId: this.detail.currentTaskId, opinion: this.opinion }).then(res => {
        this.submitting = false
        if (res && res.code === 200) { this.$message.success('已同意'); this.reload() }
      }).catch(() => { this.submitting = false })
    },
    doReject() {
      if (!this.opinion || !this.opinion.trim()) { this.$message.warning('拒绝时请填写审批意见'); return }
      this.submitting = true
      reject({ taskId: this.detail.currentTaskId, opinion: this.opinion }).then(res => {
        this.submitting = false
        if (res && res.code === 200) { this.$message.success('已拒绝'); this.reload() }
      }).catch(() => { this.submitting = false })
    },
    doRevoke() {
      this.$confirm('确认撤销该申请？', '提示', { type: 'warning' }).then(() => {
        this.submitting = true
        return revoke(this.instanceId)
      }).then(res => {
        this.submitting = false
        if (res && res.code === 200) { this.$message.success('已撤销'); this.reload() }
      }).catch(() => { this.submitting = false })
    },
    statusText(s) { return { 0: '审批中', 1: '已通过', 2: '已拒绝', 3: '已撤销' }[s] || '—' },
    statusType(s) { return { 0: 'warning', 1: 'success', 2: 'danger', 3: 'info' }[s] || 'info' },
    actionText(a) {
      return { start: '发起申请', approve: '同意', reject: '拒绝', revoke: '撤销', cc: '抄送', autoPass: '自动通过', skip: '跳过', finish: '流程结束' }[a] || a
    },
    logType(a) {
      return { start: 'primary', approve: 'success', reject: 'danger', revoke: 'info', finish: 'success', cc: 'warning' }[a] || 'primary'
    }
  }
}
</script>

<style scoped>
.wf-detail { padding: 4px; }
.wf-detail-head { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
.wf-detail-title { font-size: 18px; font-weight: 600; color: #303133; }
.wf-detail-meta { color: #909399; font-size: 13px; margin-bottom: 12px; }
.wf-sep { margin: 0 8px; color: #DCDFE6; }
.wf-detail-tabs { margin-bottom: 12px; }
.wf-log-node { font-weight: 600; color: #303133; }
.wf-log-body { font-size: 13px; color: #606266; margin-top: 2px; }
.wf-log-action { margin: 0 8px; color: #409EFF; }
.wf-log-opinion { color: #909399; }
.wf-detail-actions { border-top: 1px solid #EBEEF5; padding-top: 12px; }
.wf-action-btns { display: flex; gap: 10px; }
.wf-empty { color: #909399; text-align: center; padding: 20px 0; }
</style>
